import { MasterKey, deviceWrappingKey, type Slot } from '@realmery/vault';

/**
 * What this device keeps for unlocking realms (docs/REALM-ENCRYPTION.md section 4).
 *
 * - "Keep realms unlocked on this device": the master key wrapped under a key of the
 *   device's own, created non-extractable, so it can be used in this browser but not
 *   copied out of it.
 * - Copies of the account's passkey and recovery-key wraps, so those can unlock offline.
 *   They are the server's own copies, refreshed on every visit online, and open only with
 *   that passkey's PRF output or the recovery key.
 *
 * Nothing here reaches the server, and signing out deletes all of it.
 */
export const KEEP_UNLOCKED_DAYS = 30;
const DAY_MS = 24 * 60 * 60 * 1000;

const DB_NAME = 'realmery-device';
const UNLOCK = 'unlock';
const WRAPS = 'wraps';

interface DeviceRecord {
  readonly userId: string;
  readonly key: CryptoKey;
  readonly wrapped: string;
  readonly lastUsed: number;
}

/** Whether a record last used at `lastUsed` still unlocks at `now`. */
export function stillKept(lastUsed: number, now: number): boolean {
  return now >= lastUsed && now - lastUsed < KEEP_UNLOCKED_DAYS * DAY_MS;
}

function slot(userId: string): Slot {
  return { userId, kind: 'device' };
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 2);
    request.onupgradeneeded = () => {
      for (const name of [UNLOCK, WRAPS]) {
        if (!request.result.objectStoreNames.contains(name)) request.result.createObjectStore(name, { keyPath: 'userId' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  use: (store: IDBObjectStore) => IDBRequest<T>,
  name: string = UNLOCK,
): Promise<T> {
  const db = await openDb();
  try {
    return await new Promise<T>((resolve, reject) => {
      const tx = db.transaction(name, mode);
      const request = use(tx.objectStore(name));
      tx.oncomplete = () => resolve(request.result);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
}

/** Keeps this account's realms unlocked on this device. */
export async function keepUnlocked(userId: string, master: MasterKey, now = Date.now()): Promise<void> {
  const device = await deviceWrappingKey();
  const record: DeviceRecord = { userId, key: device.key, wrapped: await master.wrap(slot(userId), device), lastUsed: now };
  await withStore('readwrite', (store) => store.put(record));
}

/**
 * The master key, if this device keeps the account unlocked and was used in the last
 * {@link KEEP_UNLOCKED_DAYS} days. A lapsed or unusable record is deleted.
 */
export async function deviceUnlock(userId: string, now = Date.now()): Promise<MasterKey | null> {
  let record: DeviceRecord | undefined;
  try {
    record = await withStore<DeviceRecord | undefined>('readonly', (store) => store.get(userId) as IDBRequest<DeviceRecord | undefined>);
  } catch {
    return null; // no IndexedDB here, as in some private windows
  }
  if (!record) return null;
  if (!stillKept(record.lastUsed, now)) {
    await forgetDevice(userId);
    return null;
  }
  try {
    const master = await MasterKey.unwrap(record.wrapped, slot(userId), await deviceWrappingKey(record.key));
    await withStore('readwrite', (store) => store.put({ ...record, lastUsed: now }));
    return master;
  } catch {
    await forgetDevice(userId);
    return null;
  }
}

export async function isKeptUnlocked(userId: string): Promise<boolean> {
  try {
    return (await withStore('readonly', (store) => store.count(userId))) > 0;
  } catch {
    return false;
  }
}

/** Stops keeping one account unlocked here, or every account when no id is given. */
export async function forgetDevice(userId?: string): Promise<void> {
  try {
    await withStore('readwrite', (store) => (userId ? store.delete(userId) : store.clear()));
  } catch {
    // No IndexedDB: nothing was kept.
  }
}

// ---- wraps for unlocking offline ----

export interface OfflineWrap {
  readonly kind: string;
  readonly wrapped: string;
}

/**
 * The wraps worth keeping for offline use: passkeys and the recovery key. The password's
 * is left out, as it cannot open offline: OPAQUE needs the server to rebuild its key.
 */
export function offlineWraps(wraps: readonly OfflineWrap[]): OfflineWrap[] {
  return wraps
    .filter((w) => typeof w.kind === 'string' && typeof w.wrapped === 'string' && (w.kind === 'recovery' || w.kind.startsWith('passkey:')))
    .map(({ kind, wrapped }) => ({ kind, wrapped }));
}

/** Replaces this account's offline copies with the server's current wraps. */
export async function saveOfflineWraps(userId: string, wraps: readonly OfflineWrap[], now = Date.now()): Promise<void> {
  try {
    await withStore('readwrite', (store) => store.put({ userId, wraps: offlineWraps(wraps), savedAt: now }), WRAPS);
  } catch {
    // No IndexedDB: offline unlock is not available here.
  }
}

export async function loadOfflineWraps(userId: string): Promise<OfflineWrap[]> {
  try {
    const record = await withStore<{ wraps?: OfflineWrap[] } | undefined>(
      'readonly',
      (store) => store.get(userId) as IDBRequest<{ wraps?: OfflineWrap[] } | undefined>,
      WRAPS,
    );
    return offlineWraps(record?.wraps ?? []);
  } catch {
    return [];
  }
}

/** Deletes everything this device keeps: kept unlocks and offline wraps, for every account. */
export async function forgetEverything(): Promise<void> {
  await forgetDevice();
  try {
    await withStore('readwrite', (store) => store.clear(), WRAPS);
  } catch {
    // No IndexedDB: nothing was kept.
  }
}
